import { request } from "@/lib/datocms";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { createDynamicPagePaths } from "@/lib/routes/dynamic-routes-build";
import { dynamicPagesConfig } from "@/lib/dynamic-pages-config";
import { _getSiteConfiguration } from "@/dato-api/site-config-cache";
import createRouteTable, {
  getRouteTable,
} from "@/lib/routes/routes-table-handler";
import {
  getGroupsPortfolioSlugs,
  getNewPortfolioSlugs,
  getTypeSpecificPortfolioProductsListSlugs,
} from "@/dato-api/portfolio";
import {
  buildPortfolioCollectionsSlugs,
  buildPortfolioSlug,
  buildProductsSlugs,
  buildProductTypeSpecificSlugs,
} from "@/lib/functions";
import { getTypeSpecificPortfolioCollectionSlugs } from "@/dato-api/portfolio";
import {
  buildProductsListTypeSpecificSlugs,
  getProductsFromArrayReturned,
  buildProductsListSlugs,
} from "@/lib/routes/products-routes-handler";
import { getAwardsByProductsIdsList } from "@/dato-api/awards";
import { getRatingsByProductsIdsList } from "@/dato-api/ratings";
import { ApolloProvider } from "@apollo/client";
import client from "@/dato-api/apollo-client";

// can't be a fragment
export const slugInternalQuery = `
     __typename
         slug
         _allSlugLocales {
         locale
         value
     }
 `;

export async function getStaticPaths(context) {
  let siteId = 0;

  const data = await _getSiteConfiguration(context.defaultLocale, false).then(
    (website) => {
      siteId = website.siteConfiguration.id;

      return request({
        query: `
                     {
                         allBrandsPortfolios(filter: {siteConfig: {eq: "${siteId}"}}) {
                             ${slugInternalQuery}
                         }
                         allBrandsPages(filter: {siteConfig: {eq: "${siteId}"}}) {
                             ${slugInternalQuery}
                         }
                         allBrandsLegals(filter: {siteConfig: {eq: "${siteId}"}}) {
                             ${slugInternalQuery}
                         }
                         allArticles(filter: {websiteRelated: {eq: "${siteId}"}})  {
                          ${slugInternalQuery}
                        }
                        allRecipes(filter: {websiteConfiguration: {eq: "${siteId}"}})  {
                          ${slugInternalQuery}
                        }
                       }
                     `,
      });
    }
  );

  const portfolios = await getNewPortfolioSlugs(siteId);
  let dynamicPagesArray = [];
  let _portfolioSlugs = [];

  await Promise.all(
    portfolios?.allBrandsProducts?.map(async (portfolio) => {
      const portfolioSlugs = buildPortfolioSlug(
        portfolio?.slug,
        portfolio?.__typename
      );

      _portfolioSlugs.push(portfolioSlugs[0]);
      dynamicPagesArray.push(...portfolioSlugs);

      const productsList = portfolio.typeList ? true : false;

      if (!productsList) {
        const portfolioGroups = await getGroupsPortfolioSlugs(siteId);
        const collections = buildPortfolioCollectionsSlugs(
          portfolioSlugs,
          portfolioGroups?.brandsProduct
        );
        dynamicPagesArray.push(...collections);

        const typeSpecific = await getTypeSpecificPortfolioCollectionSlugs(
          siteId,
          portfolio.slug
        );
        const typeSpecificToProducts = JSON.parse(JSON.stringify(typeSpecific)); // => copy
        const typeSpecificSlugs = buildProductTypeSpecificSlugs(
          portfolioSlugs,
          typeSpecificToProducts?.brandsProduct
        );
        dynamicPagesArray.push(...typeSpecificSlugs);

        //------------------->

        const { accoladesSlug, accoladesShowAwards, accoladesShowRatings } =
          typeSpecific.brandsProduct;

        const listOfProductsAllSlugsLocales = getProductsFromArrayReturned(
          typeSpecific?.brandsProduct,
          "collection"
        );

        const awardsByProduct = [];
        const ratingsByProduct = [];
        const awardsSlugs = [];
        const ratingsSlugs = [];
        if (accoladesShowAwards || accoladesShowRatings) {
          const productsIDS = listOfProductsAllSlugsLocales.map((product) => {
            return product.id;
          });

          const cleanIDS = productsIDS.filter(
            (item, index) => productsIDS.indexOf(item) === index
          );

          const { allAwards } = await getAwardsByProductsIdsList(cleanIDS);
          const { allRatings } = await getRatingsByProductsIdsList(cleanIDS);

          listOfProductsAllSlugsLocales.map((product) => {
            allAwards.filter((award) => {
              if (award.product.id === product.id) {
                awardsByProduct.push(award);
              }
            });

            allRatings.filter((rating) => {
              if (rating.product.id === product.id) {
                ratingsByProduct.push(rating);
              }
            });
          });

          const productsWithAwardSlugIds = [];
          awardsByProduct?.map((award) => {
            if (productsWithAwardSlugIds.indexOf(award.product.id) === -1) {
              award.product._allSlugLocales.map((locale) => {
                productsWithAwardSlugIds.push(award.product.id);
                locale.value = `${accoladesSlug}/${locale.value}`;
              });
              award.product.__typename = award.__typename;
              awardsSlugs.push(award.product);
            }
          });

          const productsWithRatingSlugIds = [];
          ratingsByProduct?.map((rating) => {
            if (productsWithRatingSlugIds.indexOf(rating.product.id) === -1) {
              rating.product._allSlugLocales.map((locale) => {
                productsWithRatingSlugIds.push(rating.product.id);
                locale.value = `${accoladesSlug}/${locale.value}`;
              });
              rating.product.__typename = rating.__typename;
              ratingsSlugs.push(rating.product);
            }
          });
          // build accoladesSlugs
          const accoladesSlugs = [
            {
              _allSlugLocales: [
                {
                  locale: "pt",
                  value: accoladesSlug,
                },
                {
                  locale: "en",
                  value: accoladesSlug,
                },
                {
                  locale: "es",
                  value: accoladesSlug,
                },
              ],
              __typename: "BrandAwardRecord",
            },
          ];

          dynamicPagesArray.push(...awardsSlugs);
          dynamicPagesArray.push(...ratingsSlugs);
          dynamicPagesArray.push(...accoladesSlugs);
        }

        //------------------->

        const productsSlugs = buildProductsSlugs(
          portfolioSlugs,
          typeSpecific?.brandsProduct
        );
        dynamicPagesArray.push(...productsSlugs);
      } else {
        const typeSpecific = await getTypeSpecificPortfolioProductsListSlugs(
          siteId,
          portfolio.slug
        );
        const { accoladesSlug, accoladesShowAwards, accoladesShowRatings } =
          typeSpecific.brandsProduct;

        const listOfProductsAllSlugsLocales = getProductsFromArrayReturned(
          typeSpecific?.brandsProduct,
          "list"
        );
        const listOfProductsAllSlugsLocalesCopy1 = JSON.parse(
          JSON.stringify(listOfProductsAllSlugsLocales)
        ); // => copy

        const typeSpecificSlugs = buildProductsListTypeSpecificSlugs(
          portfolioSlugs,
          listOfProductsAllSlugsLocalesCopy1
        );

        dynamicPagesArray.push(...typeSpecificSlugs);

        const awardsByProduct = [];
        const ratingsByProduct = [];
        const awardsSlugs = [];
        const ratingsSlugs = [];

        if (accoladesShowAwards || accoladesShowRatings) {
          const productsIDS = listOfProductsAllSlugsLocales
            .map((product) => {
              return product.id;
            })
            .filter((id) => id !== undefined);

          const { allAwards } = await getAwardsByProductsIdsList(productsIDS);
          const { allRatings } = await getRatingsByProductsIdsList(productsIDS);

          listOfProductsAllSlugsLocales.map((product) => {
            allAwards.filter((award) => {
              if (award.product.id === product.id) {
                awardsByProduct.push(award);
              }
            });

            allRatings.filter((rating) => {
              if (rating.product.id === product.id) {
                ratingsByProduct.push(rating);
              }
            });
          });

          // todo: add filters based on accoladesShowAwards, accoladesShowRatings, accoladesRatings100, accoladesRatings20, accoladesAwardsType
          awardsByProduct?.map((award) => {
            award.product._allSlugLocales.map((locale) => {
              // const portfolioSlug = portfolioSlugs[0]._allSlugLocales.filter((portfolioLocale) => locale.locale === portfolioLocale.locale)

              locale.value = `${accoladesSlug}/${locale.value}`;
            });
            award.product.__typename = award.__typename;
            awardsSlugs.push(award.product);
          });

          ratingsByProduct?.map((rating) => {
            rating.product._allSlugLocales.map((locale) => {
              locale.value = `${accoladesSlug}/${locale.value}`; // validate this
            });
            rating.product.__typename = rating.__typename;
            ratingsSlugs.push(rating.product);
          });

          dynamicPagesArray.push(...awardsSlugs);
          dynamicPagesArray.push(...ratingsSlugs);
        }

        //build article slugs
        const articleSlugs = [];
        data?.allArticles?.map((article) => {
          article._allSlugLocales.map((locale) => {
            locale.value = `news/${locale.value}`;
          });
          articleSlugs.push(article);
        });

        dynamicPagesArray.push(...articleSlugs);

        // build accoladesSlugs
        const accoladesSlugs = [
          {
            _allSlugLocales: [
              {
                locale: "pt",
                value: accoladesSlug,
              },
              {
                locale: "en",
                value: accoladesSlug,
              },
              {
                locale: "es",
                value: accoladesSlug,
              },
            ],
            __typename: "BrandAwardRecord",
          },
        ];

        dynamicPagesArray.push(...accoladesSlugs);

        const productsSlugs = buildProductsListSlugs(
          portfolioSlugs,
          listOfProductsAllSlugsLocales
        );
        dynamicPagesArray.push(...productsSlugs);
      }
    })
  );

  const pathsArray = createDynamicPagePaths(
    [
      ...data?.allBrandsPortfolios,
      ...data?.allBrandsPages,
      ...data?.allBrandsLegals,
      ...data?.allRecipes,
      ..._portfolioSlugs,
      ...dynamicPagesArray,
    ],
    context,
    "",
    []
  );

  await createRouteTable(pathsArray.routersTable);

  return {
    paths: pathsArray.paths,
    fallback: false,
  };
}

export default function ContentPage({ data, relations, pageType }) {
  let Component = dynamicPagesConfig[pageType].component;

  return (
    <ApolloProvider client={client}>
      <Component data={data} relations={relations} />{" "}
    </ApolloProvider>
  );
}

export async function getStaticProps(context) {
  const siteConfig = await _getSiteConfiguration(context.locale, false);
  const routeTable = await getRouteTable();
  const path = [context.locale, ...context.params.slug].join("/");
  const pageType = routeTable[0][path];

  return dynamicPagesConfig[pageType].getStaticProps(
    context,
    pageType,
    serverSideTranslations,
    siteConfig
  );
}
